/**
 * B2：全局上传预算（压缩槽 + 上传槽），图片空间 / 工作流等共用，避免与读图/下载抢满浏览器。
 */
import { UPLOAD_CONFIG } from './uploadConfig';

/** 压缩槽并发；默认 3，可用 REACT_APP_IPE_UPLOAD_COMPRESS_CONCURRENCY 覆盖（T-A13） */
export const UPLOAD_COMPRESS_CONCURRENCY = Math.max(1, UPLOAD_CONFIG.compressConcurrency || 3);
/** OSS PUT / multipart 分片内并发；默认 4，可用 REACT_APP_IPE_UPLOAD_PUT_CONCURRENCY 覆盖 */
export const UPLOAD_SAVE_CONCURRENCY = Math.max(1, UPLOAD_CONFIG.putConcurrency || 4);
export const UPLOAD_BATCH_CHUNK_SIZE = UPLOAD_CONFIG.chunkSize || 20;
/** 多批（每批 ≤chunkSize）并行数；默认 2 */
export const UPLOAD_CHUNK_CONCURRENCY = Math.max(1, UPLOAD_CONFIG.chunkConcurrency || 2);

let compressActive = 0;
let saveActive = 0;
/** @type {Array<() => void>} */
const compressWaiters = [];
/** @type {Array<() => void>} */
const saveWaiters = [];

function pump(waiters, activeRef, max) {
  while (waiters.length > 0 && activeRef.count < max) {
    const next = waiters.shift();
    activeRef.count += 1;
    next?.();
  }
}

const compressRef = {
  get count() {
    return compressActive;
  },
  set count(v) {
    compressActive = v;
  },
};

const saveRef = {
  get count() {
    return saveActive;
  },
  set count(v) {
    saveActive = v;
  },
};

function acquire(waiters, activeRef, max) {
  return new Promise((resolve) => {
    const tryAcquire = () => {
      if (activeRef.count < max) {
        activeRef.count += 1;
        resolve();
        return;
      }
      waiters.push(tryAcquire);
    };
    tryAcquire();
  });
}

function release(waiters, activeRef, max) {
  activeRef.count = Math.max(0, activeRef.count - 1);
  pump(waiters, activeRef, max);
}

export async function withUploadCompressSlot(fn) {
  await acquire(compressWaiters, compressRef, UPLOAD_COMPRESS_CONCURRENCY);
  try {
    return await fn();
  } finally {
    release(compressWaiters, compressRef, UPLOAD_COMPRESS_CONCURRENCY);
  }
}

export async function withUploadSaveSlot(fn) {
  await acquire(saveWaiters, saveRef, UPLOAD_SAVE_CONCURRENCY);
  try {
    return await fn();
  } finally {
    release(saveWaiters, saveRef, UPLOAD_SAVE_CONCURRENCY);
  }
}

/** 有界并发 map（用于批量压缩等） */
export async function mapWithUploadConcurrency(items, concurrency, mapper) {
  const list = Array.isArray(items) ? items : [];
  const limit = Math.max(1, Math.min(concurrency || UPLOAD_COMPRESS_CONCURRENCY, list.length || 1));
  const results = new Array(list.length);
  let cursor = 0;

  async function worker() {
    while (cursor < list.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await mapper(list[index], index);
    }
  }

  await Promise.all(Array.from({ length: Math.min(limit, list.length) }, () => worker()));
  return results;
}
